import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  BILLING_PLAN_IDS,
  useCheckoutSessionStatus,
  type BillingPlanId,
} from "@/services/api/billingService"
import { Hourglass, TimerOff } from "lucide-react"
import { useEffect } from "react"
import { Link } from "react-router"
import { CheckoutSuccessCard } from "./CheckoutSuccessCard"
import { UpgradeError, UpgradeState } from "./UpgradeStates"
import { useAwaitPaidAccess } from "./useAwaitPaidAccess"

interface CheckoutResultProps {
  sessionId: string | null
  /** Open or expired checkout, or no usable session: back to the plan picker. */
  onBackToPlans: () => void
  /** The chosen plan, when known (not on /upgrade/return). */
  plan?: BillingPlanId
  /** Set right after our own confirm succeeded: an open session then means
   *  Stripe has not caught up yet, not an abandoned checkout. */
  confirmed?: boolean
}

const Activation: React.FC<{ plan?: BillingPlanId }> = ({ plan }) => {
  const { state, retry } = useAwaitPaidAccess(true)
  return <CheckoutSuccessCard plan={plan} state={state} onRetry={retry} />
}

/**
 * The one success flow, for onComplete in the page and for /upgrade/return:
 * GET the session, then wait until the paid group reached the session.
 * An open session goes back to the picker, unless our confirm just succeeded.
 */
export const CheckoutResult: React.FC<CheckoutResultProps> = ({
  sessionId,
  onBackToPlans,
  plan,
  confirmed = false,
}) => {
  const { translate } = useTranslation()
  const { data, isPending, isError, refetch } =
    useCheckoutSessionStatus(sessionId)
  const status = data?.status

  useEffect(() => {
    if (status === "open" && !confirmed) onBackToPlans()
  }, [status, confirmed, onBackToPlans])

  // A failed refetch keeps its data: the wait after a payment survives it
  if (!sessionId || (isError && !data)) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <UpgradeError
          description={translate("UPGRADE/RETURN_ERROR")}
          action={
            <div className="flex gap-2">
              {sessionId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void refetch()}
                >
                  {translate("UPGRADE/RETRY")}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onBackToPlans}>
                {translate("UPGRADE/BACK_TO_PLANS")}
              </Button>
            </div>
          }
        />
      </div>
    )
  }

  // Paid by our own confirm: wait for the plan, which the webhook grants
  if (status === "open" && confirmed) return <Activation plan={plan} />

  if (isPending || status === "open") {
    return (
      <UpgradeState
        role="status"
        icon={<Spinner className="size-6" />}
        title={translate("UPGRADE/CHECKING_PAYMENT")}
      />
    )
  }

  if (status === "complete") {
    const known = BILLING_PLAN_IDS.find((id) => id === data?.plan)
    // Async methods (e.g. SEPA) complete the session before the money arrives
    if (
      data?.paymentStatus !== "paid" &&
      data?.paymentStatus !== "no_payment_required"
    ) {
      return (
        <UpgradeState
          role="status"
          icon={<Hourglass className="size-6" aria-hidden="true" />}
          title={translate("UPGRADE/CHECKING_PAYMENT")}
          description={translate("UPGRADE/PAYMENT_PROCESSING")}
          actions={
            <Button asChild>
              <Link to="/">{translate("UPGRADE/GO_TO_APP")}</Link>
            </Button>
          }
        />
      )
    }
    return <Activation plan={known ?? plan} />
  }

  return (
    <UpgradeState
      icon={<TimerOff className="size-6" aria-hidden="true" />}
      title={translate("UPGRADE/SESSION_EXPIRED_TITLE")}
      description={translate("UPGRADE/SESSION_EXPIRED_DESCRIPTION")}
      actions={
        <Button onClick={onBackToPlans}>
          {translate("UPGRADE/BACK_TO_PLANS")}
        </Button>
      }
    />
  )
}
